import {
  SettingHeader,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';

import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import type { SettingState } from '../../types';
import { EnableCloudPanel } from '../preference/enable-cloud';
import { BlobManagementPanel } from './blob-management';
import { DesktopExportPanel } from './export';
import { PrivateStorageAdminPanel } from './private-storage-admin-panel';
import { SnapshotExportPanel } from './snapshot-export';
import { WorkspaceQuotaPanel } from './workspace-quota';
import { WorkspaceStorageBreakdownPanel } from './workspace-storage-breakdown';

export const WorkspaceSettingStorage = ({
  onCloseSetting,
  onChangeSettingState,
}: {
  onCloseSetting: () => void;
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const backendAvailable = useBackendAvailable();
  const workspacePermissionService = useService(
    WorkspacePermissionService
  ).permission;
  const isTeam = useLiveData(workspacePermissionService.isTeam$);
  const isOwner = useLiveData(workspacePermissionService.isOwner$);
  const isOwnerOrAdmin = useLiveData(
    workspacePermissionService.isOwnerOrAdmin$
  );

  const canExport = !isTeam || isOwner;
  const showOfflineNotice = workspace.flavour !== 'local' && !backendAvailable;

  return (
    <>
      <SettingHeader
        title={t['Storage']()}
        subtitle={t['com.streamlain.settings.workspace.storage.subtitle']()}
        action={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
      />
      {workspace.flavour === 'local' ? (
        <>
          <EnableCloudPanel onCloseSetting={onCloseSetting} />{' '}
          {BUILD_CONFIG.isElectron && (
            <SettingWrapper>
              <DesktopExportPanel workspace={workspace} />
            </SettingWrapper>
          )}
          <SettingWrapper>
            <SnapshotExportPanel />
          </SettingWrapper>
        </>
      ) : (
        <>
          <SettingWrapper>
            <WorkspaceQuotaPanel onChangeSettingState={onChangeSettingState} />
          </SettingWrapper>

          <SettingWrapper>
            <WorkspaceStorageBreakdownPanel />
          </SettingWrapper>

          {isOwnerOrAdmin && (
            <SettingWrapper>
              <PrivateStorageAdminPanel />
            </SettingWrapper>
          )}

          {BUILD_CONFIG.isElectron && canExport && (
            <SettingWrapper>
              <DesktopExportPanel workspace={workspace} />
            </SettingWrapper>
          )}

          {canExport && (
            <SettingWrapper>
              <SnapshotExportPanel />
            </SettingWrapper>
          )}

          {isOwnerOrAdmin && (
            <SettingWrapper>
              <BlobManagementPanel />
            </SettingWrapper>
          )}
        </>
      )}
    </>
  );
};
